import {
  Component,
  OnDestroy,
  Input,
} from "@angular/core";

import { MatDialog } from "@angular/material/dialog";

import { EMPTY, Observable, Subscription } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  getPumpTypesList,
  getSiteFccList,
  getSiteFuelPointList,
  getSiteGradeList,
  getSiteWarehouseList,
} from "src/app/store/actions/sites/sites.actions";
import {
  AppState,
  pumpTypesLoadedState,
  siteFccsLoadedState,
  siteFuelPointLoadedState,
  siteGradeListLoadedState,
  siteWarehousesLoadedState,
} from "src/app/store/reducers";

import { TranslateService } from "@ngx-translate/core";
import { FuelPointService } from "src/app/shared/services/fuelPoints/fuelpoint.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import {
  SiteFuelPointDTO,
  PumpType,
  OperationModeDTO,
} from "src/app/models/Sites/siteFuelPointDTO";
import { SiteWarehouseDTO } from "src/app/models/Sites/siteWarehouseDTO";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { SiteDeviceFccDTO } from "src/app/models/Sites/siteFccDTO";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { FuelPointFormDialogComponent } from "./dialog/fuelpoint-form-dialog.component";

@Component({
  selector: "site-fuelpoint-details",
  templateUrl: "./fuelpoint-details.component.html",
  styleUrls: ["./fuelpoint-details.component.scss"],
})
export class FuelPointDetailsComponent
  implements OnDestroy
{
  _siteCode: string;

  @Input() set siteCode(code: string) {
    this._siteCode = code;
    if (this._siteCode) {
      this.loadSiteGrades(this._siteCode);
      this.loadFuelPoints(this._siteCode);
      this.loadWarehouses(this._siteCode);
      this.loadFccs(this._siteCode);
      this.loadPumpTypes();
      this.loadOperationModes();
    }
  }

  @Input() permissionResource: string;

  fuelPointList$: Observable<SiteFuelPointDTO[]>;
  fuelPointList: SiteFuelPointDTO[];
  fuelPointSub: Subscription;
  currentFuelPoint: SiteFuelPointDTO;

  //Grades List
  siteGradeList$: Observable<SiteGradeDTO[]>;
  siteGradeList: SiteGradeDTO[];
  siteGradeSub: Subscription;

  //Warehouses List
  warehouseList$: Observable<SiteWarehouseDTO[]>;
  warehouseList: SiteWarehouseDTO[];
  warehouseSub: Subscription;

  //Pump Types list
  pumpTypeList$: Observable<PumpType[]>;
  pumpTypeList: PumpType[];
  pumpTypeSub: Subscription;

  //Fcc list
  fccList$: Observable<SiteDeviceFccDTO[]>;
  fccList: SiteDeviceFccDTO[];
  fccSub: Subscription;

  //Operation Modes list
  operationModesList$: Observable<OperationModeDTO[]>;

  isFuelPointSelected = false;

  constructor(
    private store: Store<AppState>,
    private fuelPointService: FuelPointService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    public dialog: MatDialog,
  ) {
    this.fuelPointList$ = this.store.pipe(select(siteFuelPointLoadedState));
    this.siteGradeList$ = this.store.pipe(select(siteGradeListLoadedState));
    this.warehouseList$ = this.store.pipe(select(siteWarehousesLoadedState));
    this.pumpTypeList$ = this.store.pipe(select(pumpTypesLoadedState));
    this.fccList$ = this.store.pipe(select(siteFccsLoadedState));
  }

  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }

  loadFuelPoints(code: string) {
    this.fuelPointSub = this.fuelPointList$.subscribe(
      (fuelPoints: SiteFuelPointDTO[]) => {
        this.fuelPointList = fuelPoints;
        if (fuelPoints && fuelPoints.length && this.currentFuelPoint) {
          const { code: currentFuelPointCode } = this.currentFuelPoint;
          this.currentFuelPoint = fuelPoints.find(({ code }) => code === currentFuelPointCode);
        }
        this.resetFuelPoint();
      }
    );
    this.store.dispatch(getSiteFuelPointList({ 
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }));
  }

  loadSiteGrades(code: string) {
    this.siteGradeSub = this.siteGradeList$.subscribe(
      (grades: SiteGradeDTO[]) => {
        this.siteGradeList = grades;
      }
    );
    this.store.dispatch(getSiteGradeList({ code }));
  }

  loadWarehouses(code: string) {
    this.warehouseSub = this.warehouseList$.subscribe(
      (warehouses: SiteWarehouseDTO[]) => {
        if (warehouses) {
          this.warehouseList = warehouses.filter(
            (warehouse) => warehouse.warehouseType?.code === "WET"
          );
        }
      }
    );
    this.store.dispatch(getSiteWarehouseList({ 
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }));
  }

  loadFccs(code: string) {
    this.fccSub = this.fccList$.subscribe((fccList: SiteDeviceFccDTO[]) => {
      this.fccList = fccList;
    });
    this.store.dispatch(getSiteFccList({
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }));
  }

  loadPumpTypes() {
    this.pumpTypeSub = this.pumpTypeList$.subscribe((pumpTypes: PumpType[]) => {
      this.pumpTypeList = pumpTypes;
    });
    this.store.dispatch(getPumpTypesList(null));
  }

  loadOperationModes() {
    this.operationModesList$ = this.fuelPointService.getOperationModes().pipe(
      map(({ items }) => items),
      catchError((error) => {
        this.snackbarService.show({
          message: this.translate.instant("SiteManagement.operationModesLoadError"),
          type: SnackbarType.ERROR,
        });
        return EMPTY;
      })
    );
  }

  getChipForeColor(color: string) {
    return getColorByBgColor(`#${color}`);
  }

  resetFuelPoint() {
    this.dialog.closeAll();
    this.isFuelPointSelected = false;
    this.currentFuelPoint = null;
  }
  
  selectFuelPoint(fuelpoint: SiteFuelPointDTO) {
    this.isFuelPointSelected = true;
    this.currentFuelPoint = fuelpoint;

    this.openFuelPointFormDialog(false);
  }
  
  create() {
    this.isFuelPointSelected = false;
    this.currentFuelPoint = null;

    this.openFuelPointFormDialog(true);
  }

  openFuelPointFormDialog(isCreate: boolean) {
    this.dialog.open(FuelPointFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate,
        siteCode: this._siteCode,
        currentFuelPoint: this.currentFuelPoint,
        siteGradeList: this.siteGradeList,
        warehouseList: this.warehouseList,
        pumpTypeList: this.pumpTypeList,
        fccList: this.fccList,
        operationModesList$: this.operationModesList$,
      }
    });
  }

  ngOnDestroy(): void {
    if (this.fuelPointSub) {
      this.fuelPointSub.unsubscribe();
    }
    if (this.siteGradeSub) {
      this.siteGradeSub.unsubscribe();
    }
    if (this.warehouseSub) {
      this.warehouseSub.unsubscribe();
    }
    if (this.pumpTypeSub) {
      this.pumpTypeSub.unsubscribe();
    }
    if (this.fccSub) {
      this.fccSub.unsubscribe();
    }
  }
}
